import * as React from 'react'
import { CalendarIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { formFieldBoxClass } from './form-field-box'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Calendar } from './calendar'

export interface DatePickerProps {
	value?:       Date | undefined
	onChange?:    (date: Date | undefined) => void
	placeholder?: string
	/** Optional explicit formatter; defaults to `Intl.DateTimeFormat` ru/en short. */
	format?:      (date: Date) => string
	disabled?:    boolean
	className?:   string
	/** When the picker is rendered inside a flex row alongside other inputs. */
	id?:          string
	name?:        string
	'aria-invalid'?: boolean
}

const DEFAULT_FORMATTER = new Intl.DateTimeFormat(undefined, {
	day:   '2-digit',
	month: '2-digit',
	year:  'numeric',
})

/**
 * Shadcn-style DatePicker — popover trigger styled as a form field, opens a
 * `Calendar` for single date selection. Wires up to `react-hook-form` via a
 * `Controller`'s `field` (pass `value` + `onChange`).
 */
const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
	({ value, onChange, placeholder, format, disabled, className, id, name, 'aria-invalid': ariaInvalid }, ref) => {
		const [open, setOpen] = React.useState(false)
		const formatted = value ? (format ?? DEFAULT_FORMATTER.format)(value) : ''
		return (
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					render={
						<button
							ref={ref}
							id={id}
							name={name}
							type="button"
							disabled={disabled}
							aria-invalid={ariaInvalid}
							className={cn(
								formFieldBoxClass,
								'flex items-center justify-between gap-2 text-left min-w-0 focus:outline-none',
								!value && 'text-(--color-muted-foreground)',
								disabled && 'cursor-not-allowed opacity-60',
								className,
							)}
						/>
					}
				>
					<span className="truncate">{formatted || placeholder || ''}</span>
					<CalendarIcon className="size-4 shrink-0 text-(--color-muted-foreground)" />
				</PopoverTrigger>
				<PopoverContent align="start" className="w-auto rounded-(--radius-md) bg-(--color-popover) p-0 shadow-md ring-1 ring-foreground/10">
					<Calendar
						mode="single"
						selected={value}
						onSelect={(date) => {
							onChange?.(date ?? undefined)
							setOpen(false)
						}}
						autoFocus
					/>
				</PopoverContent>
			</Popover>
		)
	},
)

DatePicker.displayName = 'DatePicker'

export { DatePicker }
